import { io, type Socket } from 'socket.io-client';
import type { Admission, Transfer, Snapshot, StateBatch, InputState, PlayerState, ActionState, Reply } from '../../../../vrroom_typescript/src/platform/contracts';

/**
 * crypto.randomUUID 只在安全上下文（HTTPS 或 localhost）暴露，公网 HTTP 部署下它是
 * undefined。crypto.getRandomValues 不受该限制，因此回退到手工拼装的 UUID v4。
 * 服务端只校验 requestId 是 1–100 字符的字符串（server.ts:23），不要求 UUID 格式。
 */
function requestId(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * 断线等待重连窗口：超时即放弃（onReconnectTimeout 通知上层，通常回登录页）。
 * 取 15s 的依据：服务端对断线成员保留 30s（reconnecting 相位 + resume 凭据，
 * runtime.ts disconnect 的 m.expires），窗口 < 宽限 ⇒ 窗口内连上必走
 * room:resume 原会话恢复；同时覆盖 pm2 重启（1–3s）与多数网络瞬断
 * （wifi 切换 5–15s）。socket.io 退避 1s→5s，窗口内约重试 5 次。
 */
const RECONNECT_WINDOW_MS = 15000;

/**
 * 加密房间的密码输入弹窗（屏幕居中，蓝玻璃风格与场景内 wm-modal 系列一致）。
 * 替代原来的 window.prompt：返回 Promise<string>，用户取消时 reject。
 */
function askRoomPassword(invalid: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
        const overlay = document.createElement('div');
        overlay.id = 'room-password-overlay';
        const card = document.createElement('div');
        card.className = 'rp-card' + (invalid ? ' rp-invalid' : '');
        const icon = document.createElement('div');
        icon.className = 'rp-icon';
        icon.textContent = '🔒';
        const title = document.createElement('h3');
        title.textContent = '进入加密房间';
        const hint = document.createElement('p');
        hint.className = 'rp-hint';
        hint.textContent = invalid ? '密码不正确，请重新输入房间密码' : '该房间已加密，请输入房主发布的房间密码';
        const input = document.createElement('input');
        input.className = 'rp-input';
        input.type = 'password';
        input.maxLength = 64;
        input.autocomplete = 'off';
        input.placeholder = '房间密码';
        const actions = document.createElement('div');
        actions.className = 'rp-actions';
        const cancel = document.createElement('button');
        cancel.className = 'rp-btn rp-cancel';
        cancel.type = 'button';
        cancel.textContent = '取消';
        const ok = document.createElement('button');
        ok.className = 'rp-btn rp-ok';
        ok.type = 'button';
        ok.textContent = '确认进入';
        actions.append(cancel, ok);
        card.append(icon, title, hint, input, actions);
        overlay.append(card);
        (document.getElementById('ui-layer') ?? document.body).append(overlay);
        const done = (value?: string) => {
            overlay.remove();
            document.removeEventListener('keydown', onKey, true);
            if (value === undefined) reject(new Error('已取消进入房间'));
            else resolve(value);
        };
        const submit = () => {
            if (!input.value) { input.focus(); card.classList.add('rp-shake'); window.setTimeout(() => card.classList.remove('rp-shake'), 400); return; }
            done(input.value);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.stopPropagation(); done(); }
            else if (e.key === 'Enter') { e.stopPropagation(); submit(); }
        };
        ok.addEventListener('click', submit);
        cancel.addEventListener('click', () => done());
        overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) done(); });
        document.addEventListener('keydown', onKey, true);
        window.setTimeout(() => input.focus(), 60);
    });
}

export class RoomSession {
    readonly socket: Socket;
    admission?: Admission;
    online = false;
    offset = 0;
    poseRevision = 1;
    private seq = 0;
    private generation = 0;
    private stopped = false;
    private busy = false;
    private acceptStream = false;
    private resumeToken = '';
    private resumeRoomId = '';
    private lastTick = -1;
    private serverEpoch = '';
    private clockTimer?: ReturnType<typeof setInterval>;
    private reconnectTimer?: ReturnType<typeof setTimeout>;
    onLoad: (admission: Admission) => Promise<void> = async () => {};
    /** 断线重连恢复（同房 resume 成功）：场景/形象/语音都在，不重跑 onLoad
     *  的全量重建——上层只做轻量恢复（重置上报节流/补拉房间物体等）。 */
    onReconnect: (admission: Admission) => Promise<void> | void = async () => {};
    /** 重连等待窗口（RECONNECT_WINDOW_MS）耗尽：RoomSession 已掐掉自动重连，
     *  上层应引导用户离开（通常提示后登出回登录页）。 */
    onReconnectTimeout: () => void = () => {};
    onSnapshot: (snapshot: Snapshot) => void = () => {};
    onStates: (batch: StateBatch) => void = () => {};
    onEvent: (name: string, data: any) => void = () => {};
    onStatus: (message: string) => void = () => {};
    onReset: (state: PlayerState) => void = () => {};
    constructor(readonly userId: number, private invite: {roomId: string; sceneId: string; invite: string}) {
        // resume 凭据按 {roomId, token} 存储：目标房间与上次所在房间不同（如新建
        // 房间后跳转 /join）时必须丢弃 resume 走 room:join，否则会被拉回旧房间、
        // 绕过新房间的密码校验。旧格式（裸 token）无法比对 roomId，同样丢弃。
        const saved = sessionStorage.getItem(`room-resume:${userId}`);
        let resumeRoomId = '';
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed.roomId === 'string' && typeof parsed.token === 'string' && parsed.roomId === invite.roomId) {
                    resumeRoomId = parsed.roomId;
                    this.resumeToken = parsed.token;
                }
            } catch { /* 裸 token 旧格式：丢弃 */ }
            if (!this.resumeToken) sessionStorage.removeItem(`room-resume:${userId}`);
        }
        this.resumeRoomId = resumeRoomId;
        this.socket = io({ autoConnect: false, transports: ['websocket'], auth: {protocolVersion: 1}, reconnectionDelay: 1000, reconnectionDelayMax: 5000 });
        this.socket.on('disconnect', reason => {
            this.online = false; this.acceptStream = false; this.generation++; this.onStatus('连接中断，正在恢复……');
            if (reason === 'io server disconnect') { this.stopped = true; this.onStatus('会话已结束，请刷新页面重新进入。'); return; }
            // 网络层断开（transport close / ping timeout）：等待窗口内靠
            // socket.io 自动重连恢复，超时交 onReconnectTimeout 终态处理。
            this.armReconnectDeadline();
        });
        this.socket.on('room:snapshot', data => { if (this.acceptStream) this.snapshot(data); });
        this.socket.on('players:state', (data: StateBatch) => {
            if (!this.matches(data) || data.serverTick <= this.lastTick) return;
            this.lastTick = data.serverTick; this.onStates(data);
        });
        for (const name of ['player:joined', 'player:left', 'player:profile', 'player:action', 'player:muted', 'player:moderator', 'player:mic-open', 'player:mic-close', 'room:objects', 'room:video', 'room:music']) {
            this.socket.on(name, data => { if (this.matches(data)) this.onEvent(name, data); });
        }
        for (const name of ['room:kicked', 'room:closed']) this.socket.on(name, data => {
            this.stopped = true; this.online = false; this.resumeToken = ''; this.clearReconnectDeadline(); sessionStorage.removeItem(`room-resume:${userId}`); this.onStatus(name === 'room:closed' ? '活动房间已关闭。' : '已离开房间，请重新打开邀请链接。'); this.onEvent(name, data);
        });
        this.socket.on('player:reset', (data: PlayerState) => this.acceptReset(data));
        this.socket.on('room:updated', data => { if (data.room?.id === this.admission?.room.id) this.onEvent('room:updated', data); });
    }
    private matches(data: {roomId: string; serverEpoch: string}) { return this.acceptStream && data.roomId === this.admission?.room.id && data.serverEpoch === this.serverEpoch; }
    private snapshot(data: Snapshot) {
        if (data.room.id !== this.admission?.room.id) return;
        if (this.serverEpoch && data.serverEpoch !== this.serverEpoch) return;
        this.serverEpoch = data.serverEpoch;
        const me = data.players.find(p => p.profile.id === this.userId);
        if (me && me.state.membershipEpoch === this.admission.membershipEpoch) { if (this.online && me.state.poseRevision > this.poseRevision) this.acceptReset(me.state); this.poseRevision = me.state.poseRevision; this.seq = Math.max(this.seq, me.state.seq); }
        this.onSnapshot(data);
    }
    private adopt(admission: Admission) {
        this.admission = admission; this.resumeToken = admission.resumeToken; this.resumeRoomId = admission.room.id; this.seq = 0; this.poseRevision = 1; this.lastTick = -1;
        sessionStorage.setItem(`room-resume:${this.userId}`, JSON.stringify({roomId: admission.room.id, token: admission.resumeToken}));
    }
    async command<T>(name: string, body: Record<string, unknown> = {}): Promise<T> {
        const request = {...body, requestId: requestId()};
        for (let attempt = 0; ; attempt++) {
            if (!this.socket.connected) throw new Error('DISCONNECTED');
            let reply: Reply<T>;
            try { reply = await this.socket.timeout(8000).emitWithAck(name, request); }
            catch (error) { if (attempt < 1 && this.socket.connected) continue; throw error; }
            if (!reply.ok) throw new Error(reply.error.code);
            return reply.data;
        }
    }
    /** 主持人放置房间物体（GLB 摆件）：服务端生成 id 并广播全员
     *  （含本人——客户端统一经 room:objects 事件路径应用，不本地先行）。 */
    async spawnRoomObject(object: Record<string, unknown>): Promise<{ id: string }> {
        return this.command<{ id: string }>('room:object-spawn', { object });
    }
    /** 主持人删除房间物体。 */
    async removeRoomObject(id: string): Promise<void> {
        await this.command('room:object-remove', { id });
    }
    /** 主持人移动物体（Gizmo 拖拽结束上行完整变换，服务端广播全员）。 */
    async moveRoomObject(id: string, transform: {
        position: [number, number, number];
        rotation: [number, number, number];
        scale: [number, number, number];
    }): Promise<void> {
        await this.command('room:object-move', { id, transform });
    }
    /** 主持人视频屏控制（play/pause/seek/show/hide/mute/unmute）：服务端
     *  更新房间内存态并广播 room:video，含本人——全部客户端统一按事件
     *  路径应用，不本地先行（与房间物体同构）。 */
    async videoControl(screenId: string, action: 'play' | 'pause' | 'seek' | 'show' | 'hide' | 'mute' | 'unmute', value?: number): Promise<void> {
        await this.command('room:video-control', { screenId, action, value });
    }
    /** 主持人背景音乐控制（play 带曲目从头播 / resume / pause / stop /
     *  volume）：服务端更新房间内存态并广播 room:music，含本人——全部
     *  客户端统一按事件路径应用，不本地先行（与视频屏同构）。volume 0–1。 */
    async musicControl(action: 'play' | 'resume' | 'pause' | 'stop' | 'volume', trackId?: string, value?: number): Promise<void> {
        await this.command('room:music-control', { action, trackId, value });
    }
    private async passwordCommand<T>(name: string, body: Record<string, unknown>): Promise<T> {
        for (;;) {
            try { return await this.command<T>(name, body); }
            catch (error) {
                if (!(error instanceof Error) || !['ROOM_PASSWORD_REQUIRED','ROOM_PASSWORD_INVALID'].includes(error.message)) throw error;
                const password = await askRoomPassword(error.message === 'ROOM_PASSWORD_INVALID');
                body.password = password;
            }
        }
    }
    async start(): Promise<void> {
        return new Promise((resolve, reject) => {
            let first = true;
            this.socket.on('connect', async () => {
                if (this.stopped) return;
                this.clearReconnectDeadline(); // WS 层已恢复；后续业务失败走既有错误路径
                const gen = ++this.generation; this.online = false; this.serverEpoch = ''; this.busy = false;
                // 重连恢复判定：admission 存在 = 此前成功进过房（可能已经传送换房）。
                // “本次要进的房间”重连时以当前所在房间为准——invite 是构造时的
                // 旧房，传送后 resume 恢复的是新房，按 invite 比对会把人拉回旧房。
                const recovering = this.admission !== undefined;
                const intended = recovering ? this.admission!.room.id : this.invite.roomId;
                let viaResume = false;
                try {
                    let admission: Admission;
                    if (this.resumeToken) {
                        try {
                            admission = await this.command('room:resume', {resumeToken: this.resumeToken});
                            viaResume = true;
                            // 防御：resume 恢复的房间与本次要进的房间不一致时（换了邀请
                            // 链接/新建房间），离开并改走 room:join，确保密码与人数校验。
                            if (admission.room.id !== intended) {
                                viaResume = false;
                                this.resumeToken = '';
                                await this.command('room:leave').catch(() => {});
                                admission = await this.passwordCommand('room:join', this.invite);
                            }
                        }
                        catch (e) { if (!(e instanceof Error) || e.message !== 'RESUME_EXPIRED') throw e; this.resumeToken = ''; admission = await this.passwordCommand('room:join', this.invite); }
                    } else admission = await this.passwordCommand('room:join', this.invite);
                    if (gen !== this.generation) return;
                    // 同房 resume 恢复走轻量路径（onReconnect）：场景/形象/远程玩家
                    // 实体断线期间不销毁，重连不该等于重新进房（旧机制每次断线都
                    // switchTo 全量重建）。resume 过期/房间不符走的 room:join 是全新
                    // 会话（spawn 重置），仍走全量 onLoad。
                    const resumeInPlace = recovering && viaResume && admission.room.id === this.admission!.room.id;
                    this.adopt(admission); await this.measureClock(); if (gen !== this.generation) return; await (resumeInPlace ? this.onReconnect(admission) : this.onLoad(admission));
                    if (gen !== this.generation) return;
                    const snapshot = await this.command<Snapshot>('player:ready', {membershipEpoch: admission.membershipEpoch});
                    if (gen !== this.generation) return;
                    this.acceptStream = true; this.snapshot(snapshot);
                    this.online = true; this.onStatus(`已连接「${admission.room.name}」`);
                    if (!this.clockTimer) this.clockTimer = setInterval(() => { void this.measureClock(); }, 10000);
                    if (first) { first = false; resolve(); }
                } catch (e) {
                    if (gen !== this.generation) return;
                    this.onStatus(`进入失败：${e instanceof Error ? e.message : e}`);
                    void this.command('room:leave').catch(() => {});
                    if (first) { first = false; reject(e); }
                }
            });
            this.socket.on('connect_error', error => { this.onStatus(`连接失败：${error.message}`); if (['AUTH_REQUIRED','PROTOCOL_MISMATCH'].includes(error.message) && first) { first = false; reject(error); } });
            this.socket.connect();
        });
    }
    private async measureClock() {
        const start = Date.now();
        try { const server: number = await this.socket.timeout(3000).emitWithAck('time:ping'); this.offset = server - (start + Date.now()) / 2; } catch { /* reconnect owns failures */ }
    }
    /** 断线起计时：RECONNECT_WINDOW_MS 内未重连成功即放弃——掐掉 socket.io
     *  自动重连（stopped + disconnect），作废进行中的恢复流程（generation++），
     *  交 onReconnectTimeout 终态处理（上层提示后回登录页）。任何一次
     *  connect（含成功的业务握手）都会 clear。 */
    private armReconnectDeadline(): void {
        this.clearReconnectDeadline();
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = undefined;
            this.stopped = true;
            this.generation++;
            this.socket.disconnect();
            this.onStatus('重连超时，已断开连接');
            this.onReconnectTimeout();
        }, RECONNECT_WINDOW_MS);
    }
    private clearReconnectDeadline(): void {
        if (this.reconnectTimer === undefined) return;
        clearTimeout(this.reconnectTimer); this.reconnectTimer = undefined;
    }
    get serverTime() { return Date.now() + this.offset; }
    publish(state: Omit<InputState, 'membershipEpoch'|'poseRevision'|'seq'>) {
        if (!this.online || !this.admission || this.busy) return;
        this.socket.volatile.emit('player:state', {...state, membershipEpoch: this.admission.membershipEpoch, poseRevision: this.poseRevision, seq: ++this.seq});
    }
    async action(actionId: string | null): Promise<ActionState> {
        if (!this.online) throw new Error('尚未进入房间');
        return this.command('player:action', {actionId, membershipEpoch: this.admission!.membershipEpoch});
    }
    private acceptReset(state: PlayerState) {
        if (state.membershipEpoch !== this.admission?.membershipEpoch || state.poseRevision <= this.poseRevision) return;
        this.poseRevision = state.poseRevision; this.seq = Math.max(this.seq, state.seq); this.onReset(state);
    }
    async reset() { if (this.online) this.acceptReset(await this.command('player:reset', {membershipEpoch: this.admission!.membershipEpoch})); }
    async transfer(portalId: string) {
        if (!this.online || this.busy) return;
        this.busy = true; const gen = this.generation;
        try {
            const target = await this.passwordCommand<Transfer>('portal:prepare', {portalId, membershipEpoch: this.admission!.membershipEpoch});
            if (gen !== this.generation) return;
            this.online = false; this.acceptStream = false;
            await this.onLoad(target);
            if (gen !== this.generation) return;
            const committed = await this.command<Admission>('portal:commit', {transferId: target.transferId});
            if (gen !== this.generation) return;
            this.adopt(committed);
            const sync = await this.command<{snapshot: Snapshot}>('session:sync');
            if (gen !== this.generation) return;
            this.acceptStream = true; this.snapshot(sync.snapshot);
            this.online = true; this.onStatus(`已进入「${committed.room.name}」`);
        } catch (e) {
            if (gen !== this.generation) return;
            // An ACK can be lost after commit. Ask the server before rolling anything back.
            try {
                let sync = await this.command<{admission: Admission; snapshot: Snapshot; phase: string}>('session:sync');
                if (gen !== this.generation) return;
                if (sync.phase === 'transferring') { await this.command('portal:cancel'); if (gen !== this.generation) return; sync = await this.command('session:sync'); }
                if (gen !== this.generation) return;
                this.acceptStream = false; this.adopt(sync.admission); await this.onLoad(sync.admission);
                if (gen !== this.generation) return;
                this.acceptStream = true; this.snapshot(sync.snapshot); this.online = sync.phase === 'online';
            } catch { if (gen !== this.generation) return; this.online = false; }
            this.onStatus(`传送未完成：${e instanceof Error ? e.message : e}`);
        } finally { if (gen === this.generation) this.busy = false; }
    }
    dispose() { this.stopped = true; this.clearReconnectDeadline(); clearInterval(this.clockTimer); this.socket.disconnect(); }
}
